import { RefreshCw, X, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { panel } from '@/components/surface/tones';
import { Skeleton } from '@/components/surface/ui';

/**
 * The page's last error, pinned to the corner of the screen.
 *
 * Every action on this page reports into one error message. At the top of a long page it was
 * invisible to somebody who had scrolled down to the row they clicked, which made a refused action
 * look like a button that did nothing. Pinned, it is seen wherever they are, and stays until it is
 * dismissed or the next action succeeds.
 */
export function ErrorToast({ message, onDismiss }: { message: string | null; onDismiss: () => void }) {
  if (message === null) {
    return null;
  }

  return (
    <div
      role="alert"
      className={cn(
        panel,
        'fixed inset-x-4 bottom-4 z-40 mx-auto flex max-w-md items-start gap-3 p-4 animate-in fade-in-0 slide-in-from-bottom-2 sm:inset-x-auto sm:right-6 sm:mx-0',
        '!shadow-[0_16px_40px_-12px_rgb(15_23_42/0.35)] dark:!shadow-[0_16px_40px_-8px_rgb(0_0_0/0.8)]',
      )}
    >
      <XCircle className="mt-0.5 size-4 shrink-0 text-danger" aria-hidden />
      <p className="min-w-0 flex-1 text-sm">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="-m-1 rounded-md p-1 text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
      >
        <X className="size-4" aria-hidden />
      </button>
    </div>
  );
}

/** Shaped like the page it stands in for, so nothing jumps when the data arrives. */
export function PageSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading backups">
      <Skeleton className="h-28" />
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
        <Skeleton className="h-56" />
        <Skeleton className="h-56" />
        <Skeleton className="h-56" />
      </div>
      <Skeleton className="h-80" />
    </div>
  );
}

export function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className={cn(panel, 'flex flex-col items-start gap-3 p-5 sm:flex-row sm:items-center')}>
      <XCircle className="size-5 shrink-0 text-danger" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">Backups could not be loaded</p>
        <p className="text-[13px] text-muted-foreground">{message}</p>
      </div>
      <Button size="sm" variant="ghost" onClick={onRetry} className="bg-foreground/[0.05] hover:bg-foreground/[0.09]">
        <RefreshCw />
        Try again
      </Button>
    </div>
  );
}
